import {Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogModule, MatDialogRef} from '@angular/material/dialog';
import {CecTable, CecTableAdminStatus, CecTableStatus, CecTableType} from '../../../common/types/cec-table';
import {FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, UntypedFormGroup, Validators} from '@angular/forms';
import { Subscription } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule, MatHint, MatLabel } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TableManagerData } from '../models/table-manager-data.interface';

@Component({
  selector: 'table-manager-dialog',
  templateUrl: './table-manager-dialog.component.html',
  styleUrls: ['./table-manager-dialog.component.scss'],
  standalone: true,
  imports: [MatDialogModule, TranslateModule, MatCheckboxModule, FormsModule, MatLabel, MatFormFieldModule, MatHint, ReactiveFormsModule, MatInputModule, MatButtonModule, MatIconModule, MatTooltipModule]
})
export class TableManagerDialogComponent implements OnInit, OnDestroy {

  /** Formgroup reference for checkboxes */
  protected formGroup: UntypedFormGroup;

  /** The table input value reference */
  protected value = '';

  /** Subscription for handling formgroup changes */
  private sub: Subscription;

  constructor(@Inject(MAT_DIALOG_DATA) public data: TableManagerData, private dialogRef: MatDialogRef<TableManagerDialogComponent>, private builder: FormBuilder) {
    /** initializes form group */
    this.formGroup = this.builder.group({
      lifeStamp: false,
      yesOrNot: false,
      or: false,
      orTable: null,
      cancelled: false
    });

    /** subscribes to the formgroup observable */
    this.listenToFormChange(this.formGroup);
  }

  ngOnInit(): void {
    /** inserts value of table content */
    this.value = this.data?.table?.content;
    /** inserts value of or table and evaluates checkboxes */
    this.formGroup.patchValue({ 'orTable': this.data?.table?.orTable });
    this.evaluateCheckboxes(this.data?.table);
  }

  ngOnDestroy(): void {
    /** unsubscribes from observable */
    this.sub?.unsubscribe();
  }

  /**
   * Listens to checkbox form changes and disables checkboxes according to business logic
   * @param formGroup the checkbox formgroup reference
   */
  private listenToFormChange(formGroup: FormGroup): void {
    this.sub = formGroup.valueChanges.subscribe(() => {
      const checked = this.formContainsCheckedValues(formGroup);
      this.sub.unsubscribe();
      if (!checked) {
        for (const field in this.formGroup.controls) {
          this.formGroup.controls[field].enable();
        }
      } else {
        for (const field in this.formGroup.controls) {
          if (!this.formGroup.controls[field].value && field !== 'lifeStamp' && field !== 'orTable') {
            this.formGroup.controls[field].disable();
          }
        }
      }

      this.handleOrTableValidator(formGroup);

      /** re-subscribes to the formgroup observable */
      this.listenToFormChange(formGroup);
    });
  }

  /**
   * Verifies if the form contains checked checkboxes
   * @param formGroup the checkbox formgroup reference
   * @returns true/false as formgroup checked status
   */
  private formContainsCheckedValues(formGroup: FormGroup): boolean {
    return Object.keys(formGroup.controls).some(
      (formKey) => formKey !== 'lifeStamp' && formKey !== 'orTable' && formGroup.controls[formKey].value
    );
  }

  private handleOrTableValidator(formGroup: FormGroup): void {
    if (!formGroup.get('or').value) {
      formGroup.get('orTable').removeValidators(Validators.required);
      formGroup.get('orTable').updateValueAndValidity();
      return;
    }

    formGroup.get('orTable').addValidators(Validators.required);
    formGroup.get('orTable').updateValueAndValidity();
  }

  /**
   * Evaluates checkboxes in process of dialog opening
   * @param cecTable for evaluation of desired CecTable
   */
  private evaluateCheckboxes(cecTable: CecTable): void {
    /** if cec table is not present, do nothing */
    if (!cecTable) {
      return;
    }

    /** prefills checkboxes according to cec table type */
    if (cecTable.type === CecTableType.LIFESTAMP) { this.formGroup.patchValue({ 'lifeStamp': true }); }
    if (cecTable.adminStatus === CecTableAdminStatus.YESORNOT) { this.formGroup.patchValue({ 'yesOrNot': true }); }
    if (cecTable.adminStatus === CecTableAdminStatus.OR) { this.formGroup.patchValue({ 'or': true });}
    if (cecTable.adminStatus === CecTableAdminStatus.CANCELLED) { this.formGroup.patchValue({ 'cancelled': true }); }
  }

  /**
   * Evaluates the cec table type (life stamp/normal)
   */
  private getTableType(): CecTableType {
    return this.formGroup.get('lifeStamp').value ? CecTableType.LIFESTAMP : CecTableType.NORMAL;
  }

  /**
   * Evaluates the cec table status
   */
  private getTableStatus(): CecTableAdminStatus {
    if (this.formGroup.get('yesOrNot').value) {
      return CecTableAdminStatus.YESORNOT;
    } else if (this.formGroup.get('cancelled').value) {
      return CecTableAdminStatus.CANCELLED;
    } else if (this.formGroup.get('or').value) {
      return CecTableAdminStatus.OR;
    } else {
      return CecTableAdminStatus.OK;
    }
  }

  /**
   * Inserts tables into the table list
   */
  protected saveTable(): void {
    /** merges all spaces and trims the final string before processing */
    this.value = this.value.replace(/\s\s+/g, ' ').trim();

    /** if dialog is type single, return single table */
    if (this.data.type === 'single') {
      /** closes dialog with processed single table object */
      this.dialogRef.close(new CecTable(this.value, this.getTableType(), this.getTableStatus(), CecTableStatus.OK, true));
      return;
    }

    /** splits the tables by space identfier */
    const tables = this.value.split(' ');
    const cecTables: CecTable[] = [];
    for (const table of tables) {
      const tableName = table.replace(/[{()}]/g, '');
      /** processes the tables to table objects and pushes them into array */
      cecTables.push(new CecTable(tableName, this.getTableType(), this.getTableStatus(), CecTableStatus.OK, true));
    }

    /** closes dialog with processed multiple table objects */
    this.dialogRef.close(cecTables);
  }
}
